/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description EligibilityRequestForm Schema
 */
import _ from 'lodash';
import { FormCollapse } from '@formily/antd-v5';

import dateParser from 'lib/dateParser';
import { getNumberInput } from 'utils/workflow';
import {
	COVERAGE_FORM_TYPE,
	disableFutureDates,
	getProviderIdentifiersById,
	PATIENT_FORM_TYPE,
} from 'utils/eligibility/eligibilityRequestForm';
import { YEAR_MONTH_DATE_FORMAT, MONTH_DATE_YEAR_SLASH_FORMATS } from '@veritable/common/constants';
import { FIELD_VALIDATORS } from '@veritable/common/constants/index';
import { ANT_FORMILY_CARD_CLASS, ANT_FORMILY_FORM_WRAPPER_CLASS } from '@veritable/common/constants/antd';
import {
	DEFAULT_ELIGIBILITY_SERVICE_TYPE_CODE,
	DEFAULT_MAX_SERVICE_TYPE_COUNT,
	DEFAULT_MIN_SERVICE_TYPE_COUNT,
	ELIGIBILITY_FORM_FIELDS,
	ELIGIBILITY_GENDER_OPTIONS,
	ELIGIBILITY_PATIENT_RELATIONSHIP_OPTIONS,
	STATE_CODE_OPTIONS,
} from 'app-constants/eligibility/index';
import patientRelationshipOptions from 'app-constants/patientRelationship.json';

const searchFieldLabelClass = 'search-field';

/**
 * provider identifier keys
 */
const providerIdentifierKeys: Record<
	keyof EligibilityRequestForm.IProviderIdentifierKeys,
	keyof EligibilityRequestForm.IProviderIdentifierKeys
> = {
	stateLicenseNumber: 'stateLicenseNumber',
	facilityIDNumber: 'facilityIDNumber',
	personalIdentificationNumber: 'personalIdentificationNumber',
	contractNumber: 'contractNumber',
	electronicDevicePINNumber: 'electronicDevicePINNumber',
	submitterIdentificationNumber: 'submitterIdentificationNumber',
	userIdentification: 'userIdentification',
	providerPlanNetworkIDNumber: 'providerPlanNetworkIDNumber',
	facilityNetworkIdentificationNumber: 'facilityNetworkIdentificationNumber',
	priorIdentifierNumber: 'priorIdentifierNumber',
};

// required fields mapping
const requiredFieldsMapping: Record<keyof EligibilityRequestForm.IFormModel, string> = {
	...providerIdentifierKeys,
	npi: 'npi',
	locationId: 'locationId',
	payerId: 'payerId',
	relation: 'subscriberRelationship',
	memberId: 'memberId',
	ssn: 'patientSSN',
	medicaidId: 'medicaidId',
	groupNumber: 'groupNumber',
	firstName: 'firstName',
	lastName: 'lastName',
	dateOfBirth: 'birthDate',
	gender: 'genderCode',
	state: 'patientState',
	serviceStartDate: 'asOfDate',
	serviceEndDate: 'toDate',
	serviceType: 'serviceType',
	patientReqFields: 'patientReqFields',
	providerReqFields: 'providerReqFields',
};

// fields config mapping
const fieldsConfigMapping: Record<keyof EligibilityRequestForm.IFormModel, string> = _.mapValues(requiredFieldsMapping, (value) =>
	_.toLower(value),
);

// index for patient fields
const patientFieldsIndex = {
	patientReqFields: 0,
	memberId: 1,
	firstName: 2,
	lastName: 3,
	dateOfBirth: 4,
	gender: 5,
	ssn: 6,
	medicaidId: 7,
	groupNumber: 8,
	state: 9,
	relation: 10,
};

/**
 *
 * path to patient section
 */

const patientSectionPath = ['properties', 'formWrapper', 'properties', 'patientDetails', 'properties', 'fieldsWrapper', 'properties'];

/**
 *
 * path to provider section
 */

const providerSectionPath = ['properties', 'formWrapper', 'properties', 'provider', 'properties', 'fieldsWrapper', 'properties'];

/**
 * schema & model keys generator for a function
 */
const schemaKeys: Record<keyof EligibilityRequestForm.IFormModel, string> = {
	...providerIdentifierKeys,
	npi: 'npi',
	locationId: 'locationId',
	payerId: 'payerId',
	relation: 'relation',
	memberId: 'memberId',
	ssn: 'ssn',
	medicaidId: 'medicaidId',
	groupNumber: 'groupNumber',
	firstName: 'firstName',
	lastName: 'lastName',
	dateOfBirth: 'dateOfBirth',
	gender: 'gender',
	state: 'state',
	serviceStartDate: 'serviceStartDate',
	serviceEndDate: 'serviceEndDate',
	serviceType: 'serviceType',
	patientReqFields: 'patientReqFields',
	providerReqFields: 'providerReqFields',
};

/**
 * form fields to translate
 */
const formFieldsToTranslate: string[] = [
	..._.values(providerIdentifierKeys),
	schemaKeys.memberId,
	schemaKeys.ssn,
	schemaKeys.medicaidId,
	schemaKeys.groupNumber,
	schemaKeys.firstName,
	schemaKeys.lastName,
];
/**
 * get required fields for schema
 */
const getRequiredFields = (fieldsCombination?: string): Record<keyof EligibilityRequestForm.IFormModel, boolean> => {
	const requiredFieldsForPayer = JSON.parse(fieldsCombination || JSON.stringify('')) as string[];
	const defaultProviderIdentifierMapping = _.mapValues(providerIdentifierKeys, () => false);
	const defaultMapping: Record<keyof EligibilityRequestForm.IFormModel, boolean> = {
		...defaultProviderIdentifierMapping,
		npi: true,
		locationId: false,
		payerId: true,
		memberId: true,
		ssn: true,
		medicaidId: true,
		groupNumber: true,
		firstName: true,
		lastName: true,
		state: true,
		dateOfBirth: true,
		gender: false,
		relation: false,
		serviceStartDate: true,
		serviceEndDate: false,
		serviceType: true,
		patientReqFields: false,
		providerReqFields: false,
	};

	if (!_.isEmpty(requiredFieldsForPayer)) {
		requiredFieldsForPayer.push(requiredFieldsMapping.npi);
		requiredFieldsForPayer.push(requiredFieldsMapping.payerId);
		requiredFieldsForPayer.push(requiredFieldsMapping.serviceStartDate);
		// VER-252 serviceType is always required
		requiredFieldsForPayer.push(requiredFieldsMapping.serviceType);
		return _.mapValues(defaultMapping, (__, field) => {
			return _.includes(requiredFieldsForPayer, _.get(requiredFieldsMapping, field));
		});
	}
	//
	return defaultMapping;
};

// function to get required fields combination
const getRequiredFieldsCombination = (combinations?: string[][]): EligibilityRequestForm.IOptionProps[] => {
	let options = [] as EligibilityRequestForm.IOptionProps[];
	const invertedRequiredFieldsMapping = _.invert(requiredFieldsMapping);
	const getFieldLabel = (key?: string) => _.get(ELIGIBILITY_FORM_FIELDS, key || '')?.title;
	if (combinations) {
		options = _.map(combinations, (combination) => ({
			label: _.join(
				_.map(combination, (word) => getFieldLabel(_.get(invertedRequiredFieldsMapping, word)) || word),
				', ',
			),
			value: JSON.stringify(combination),
		}));
	}
	return options;
};

const serviceTypeValidator = (
	value: string[],
	{ defaultServiceTypeValue, maxServiceTypeCount }: { defaultServiceTypeValue: string[]; maxServiceTypeCount: number },
): string | null => {
	const list: string[] = JSON.parse(JSON.stringify(value));
	if (maxServiceTypeCount > 1 && _.size(list) >= maxServiceTypeCount && !_.includes(list, _.first(defaultServiceTypeValue))) {
		return ELIGIBILITY_FORM_FIELDS.serviceType.validationMessage || '';
	}
	return null;
};

const validateServiceStartDate = (value: string, serviceEndDate?: string, isFutureDateAllowed?: boolean): string | undefined => {
	if (!value) {
		return undefined;
	}
	const startDate = dateParser(value, YEAR_MONTH_DATE_FORMAT).startOf('day');
	const today = dateParser().startOf('day');
	if (!isFutureDateAllowed && startDate.isAfter(today)) {
		return 'Service start date cannot be in the future.';
	}
	if (serviceEndDate) {
		const endDate = dateParser(serviceEndDate, YEAR_MONTH_DATE_FORMAT).startOf('day');
		if (endDate.isValid() && startDate.isAfter(endDate)) {
			return 'Service start date cannot be after service end date.';
		}
	}
	return undefined;
};

const validateServiceEndDate = (value: string, serviceStartDate?: string, isFutureDateAllowed?: boolean): string | undefined => {
	if (!value) {
		return undefined;
	}
	const endDate = dateParser(value, YEAR_MONTH_DATE_FORMAT).startOf('day');
	const today = dateParser().startOf('day');
	if (!isFutureDateAllowed && endDate.isAfter(today)) {
		return 'Service end date cannot be in the future.';
	}
	if (serviceStartDate) {
		const startDate = dateParser(serviceStartDate, YEAR_MONTH_DATE_FORMAT).startOf('day');
		if (startDate.isValid() && endDate.isBefore(startDate)) {
			return 'Service end date cannot be before service start date.';
		}
	}
	return undefined;
};

// function to create two column form field row
const createFormFieldRow = (key: string, fields: AnyRecord, xIndex?: number): AnyRecord => {
	return {
		[key]: {
			type: 'void',
			'x-component': 'FormGrid',
			'x-index': xIndex,
			'x-component-props': { minColumns: 2, maxColumns: 2 },
			properties: fields,
		},
	};
};

/**
 * generate schema & model for a single item
 */
const formSections = {
	provider: {
		key: 'provider',
		title: 'Provider/Organization Details',
	},
	payer: {
		key: 'payer',
		title: 'Payer Details',
	},
	patientDetails: {
		key: 'patientDetails',
		title: 'Patient Details',
	},
	encounterDetails: {
		key: 'encounterDetails',
		title: 'Encounter Details',
	},
};

const getProviderIdentifierKeys = (): Record<keyof EligibilityRequestForm.IProviderIdentifierKeys, AnyType> => {
	return {
		stateLicenseNumber: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.stateLicenseNumber.title,
			'x-component': 'Input',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.stateLicenseNumber.placeholder,
			},
			'x-decorator': 'FormItem',
		},
		facilityIDNumber: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.facilityIDNumber.title,
			'x-component': 'Input',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.facilityIDNumber.placeholder,
			},
			'x-decorator': 'FormItem',
		},
		personalIdentificationNumber: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.personalIdentificationNumber.title,
			'x-component': 'Input',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.personalIdentificationNumber.placeholder,
			},
			'x-decorator': 'FormItem',
		},
		contractNumber: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.contractNumber.title,
			'x-component': 'Input',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.contractNumber.placeholder,
			},
			'x-decorator': 'FormItem',
		},
		electronicDevicePINNumber: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.electronicDevicePINNumber.title,
			'x-component': 'Input',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.electronicDevicePINNumber.placeholder,
			},
			'x-decorator': 'FormItem',
		},
		submitterIdentificationNumber: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.submitterIdentificationNumber.title,
			'x-component': 'Input',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.submitterIdentificationNumber.placeholder,
			},
			'x-decorator': 'FormItem',
		},
		userIdentification: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.userIdentification.title,
			'x-component': 'Input',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.userIdentification.placeholder,
			},
			'x-decorator': 'FormItem',
		},
		providerPlanNetworkIDNumber: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.providerPlanNetworkIDNumber.title,
			'x-component': 'Input',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.providerPlanNetworkIDNumber.placeholder,
			},
			'x-decorator': 'FormItem',
		},
		facilityNetworkIdentificationNumber: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.facilityNetworkIdentificationNumber.title,
			'x-component': 'Input',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.facilityNetworkIdentificationNumber.placeholder,
			},
			'x-decorator': 'FormItem',
		},
		priorIdentifierNumber: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.priorIdentifierNumber.title,
			'x-component': 'Input',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.priorIdentifierNumber.placeholder,
			},
			'x-decorator': 'FormItem',
		},
	};
};

const getPatientKeys = (eligibilityMeta: Payers.IEligibilityMeta, disablePatientFields?: boolean) => {
	const memberIdTooltipList = _.get(eligibilityMeta?.fieldNotes, fieldsConfigMapping.memberId);
	const memberIdTooltip = _.size(memberIdTooltipList) > 1 ? memberIdTooltipList : memberIdTooltipList?.[0];
	return {
		[schemaKeys.memberId]: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.memberId.title,
			'x-component': 'TooltipWrapper',
			'x-component-props': {
				component: 'Input',
				tooltipText: memberIdTooltip,
				onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
					e.target.value = _.trim(_.toUpper(e.target.value));
				},
				placeholder: ELIGIBILITY_FORM_FIELDS.memberId.placeholder,
			},
			'x-decorator': 'FormItem',
			'x-validator': [FIELD_VALIDATORS.memberId],
			'x-disabled': disablePatientFields,
		},

		[schemaKeys.gender]: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.gender.title,
			'x-component': 'Select',
			'x-decorator': 'FormItem',
			'x-component-props': {
				options: ELIGIBILITY_GENDER_OPTIONS,
				placeholder: ELIGIBILITY_FORM_FIELDS.gender.placeholder,
				allowClear: true,
			},
			'x-disabled': disablePatientFields,
		},

		[schemaKeys.ssn]: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.ssn.title,
			'x-component': 'Input',
			'x-component-props': {
				onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
					e.target.value = getNumberInput(e.target.value);
				},
				maxLength: 9,
				placeholder: ELIGIBILITY_FORM_FIELDS.ssn.placeholder,
			},
			'x-decorator': 'FormItem',
			'x-validator': [FIELD_VALIDATORS.ssn],
			'x-disabled': disablePatientFields,
		},

		[schemaKeys.medicaidId]: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.medicaidId.title,
			'x-component': 'Input',
			'x-component-props': {
				onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
					e.target.value = _.trim(_.toUpper(e.target.value));
				},
				placeholder: ELIGIBILITY_FORM_FIELDS.medicaidId.placeholder,
			},
			'x-decorator': 'FormItem',
			'x-disabled': disablePatientFields,
		},

		[schemaKeys.groupNumber]: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.groupNumber.title,
			'x-component': 'Input',
			'x-component-props': {
				onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
					e.target.value = _.trim(_.toUpper(e.target.value));
				},
				placeholder: ELIGIBILITY_FORM_FIELDS.groupNumber.placeholder,
			},
			'x-decorator': 'FormItem',
			'x-disabled': disablePatientFields,
		},
		[schemaKeys.state]: {
			type: 'string',
			title: ELIGIBILITY_FORM_FIELDS.state.title,
			'x-component': 'Select',
			'x-component-props': {
				placeholder: ELIGIBILITY_FORM_FIELDS.state.placeholder,
				allowClear: true,
				optionFilterProp: 'label',
				options: STATE_CODE_OPTIONS,
				showSearch: true,
			},
			'x-decorator': 'FormItem',
			'x-disabled': disablePatientFields,
		},
	};
};

const updateProviderSection = (
	schema: AnyType,
	requiredFields: Record<keyof EligibilityRequestForm.IProviderIdentifierKeys, boolean>,
	identifierMeta?: EligibilityRequestForm.IProviderIdentifierStatusModel,
): void => {
	const providerKeys = getProviderIdentifierKeys();
	const requiredFieldsItem = _.pick(_.cloneDeep(providerKeys), _.keys(_.pickBy(requiredFields, _.identity))) as AnyRecord;
	_.forEach(requiredFieldsItem, (item, key) => {
		requiredFieldsItem[key] = {
			...item,
			'x-validator': [
				...(item?.['x-validator'] ?? []),
				{
					required: true,
					message: _.get(ELIGIBILITY_FORM_FIELDS, key).errorMessage,
				},
				{
					validator: (value: string) => {
						const providerIdentifierStatus = _.get(identifierMeta, key);
						if (providerIdentifierStatus?.status === false && providerIdentifierStatus?.value === value) {
							return `This ${item?.title || ''} is not valid`;
						}
						return null;
					},
				},
			],
		};
	});
	const providerSection = _.get(schema, providerSectionPath);
	const allFields = { ...providerSection, ...requiredFieldsItem };
	const searchReqFieldRow = createFormFieldRow(
		'providerReqFieldRow',
		{
			[schemaKeys.providerReqFields]: allFields?.[schemaKeys.providerReqFields],
		},
		0,
	);
	const otherFields = _.omit(allFields, [schemaKeys.providerReqFields]);
	const otherFieldRows = createFormFieldRow('otherFieldRows', otherFields, 1);
	_.set(schema, providerSectionPath, { ...searchReqFieldRow, ...otherFieldRows });
};

const updatePatientSection = (
	schema: AnyType,
	requiredFields: Record<keyof EligibilityRequestForm.IFormModel, boolean>,
	eligibilityMeta: Payers.IEligibilityMeta,
	disablePatientFields?: boolean,
	formValues?: EligibilityRequestForm.IFormModel,
): void => {
	const patientKeys = getPatientKeys(eligibilityMeta, disablePatientFields);
	const requiredFieldsItem = _.pick(_.cloneDeep(patientKeys), _.keys(_.pickBy(requiredFields, _.identity))) as AnyRecord;

	_.forEach(requiredFieldsItem, (item, key) => {
		requiredFieldsItem[key] = {
			...item,
			'x-validator': [
				...(item?.['x-validator'] ?? []),
				{
					required: true,
					message: _.get(ELIGIBILITY_FORM_FIELDS, key).errorMessage,
				},
			],
			'x-index': _.get(patientFieldsIndex, key),
		};
	});

	const patientSection = _.omit(_.get(schema, patientSectionPath), _.keys(patientKeys));
	const extraFields = _.omit(patientSection, _.keys(schemaKeys));
	const reqPatientFields = { ..._.omit(patientSection, [..._.keys(extraFields), schemaKeys.patientReqFields]), ...requiredFieldsItem };
	let nonRequiredFieldsItem = _.omit(_.cloneDeep(patientKeys), _.keys(requiredFieldsItem)) as AnyRecord;

	if (disablePatientFields && formValues) {
		const isPayerSelected = !!_.get(formValues, schemaKeys.payerId);
		const patientName = [_.get(formValues, schemaKeys.firstName, ''), _.get(formValues, schemaKeys.lastName, '')]
			.filter(Boolean)
			.join(' ');

		const getLabelForValue = (key: string, value: string): string => {
			if (key === schemaKeys.gender) {
				const base = (_.find(ELIGIBILITY_GENDER_OPTIONS, { value }) as { label?: string } | undefined)?.label ?? value;
				return patientName ? `${base} (${patientName})` : base;
			}
			if (key === schemaKeys.state) {
				return _.find(STATE_CODE_OPTIONS as AnyType[], { value })?.label ?? value;
			}
			if (key === schemaKeys.relation) {
				return (
					_.find(patientRelationshipOptions, { value })?.label ??
					(_.find(ELIGIBILITY_PATIENT_RELATIONSHIP_OPTIONS, { value }) as { label?: string } | undefined)?.label ??
					value
				);
			}
			return value;
		};

		// For required gender, override the selected option label to include patient name
		if (requiredFieldsItem[schemaKeys.gender] && patientName) {
			const genderValue = _.get(formValues, schemaKeys.gender) as string;
			requiredFieldsItem[schemaKeys.gender] = {
				...requiredFieldsItem[schemaKeys.gender],
				'x-component-props': {
					..._.get(requiredFieldsItem[schemaKeys.gender], 'x-component-props', {}),
					options: _.map(ELIGIBILITY_GENDER_OPTIONS, (opt) => ({
						...opt,
						label: opt.value === genderValue ? `${opt.label} (${patientName})` : opt.label,
					})),
				},
			};
		}

		_.forEach(reqPatientFields, (item, key) => {
			if (requiredFields[key as keyof EligibilityRequestForm.IFormModel]) {
				return;
			}
			const fieldValue = _.get(formValues, key);
			if (_.isNil(fieldValue) || fieldValue === '') {
				return item;
			}
			reqPatientFields[key] = {
				...(item as AnyRecord),
				'x-component': 'Select',
				'x-component-props': {
					options: [{ label: getLabelForValue(key, fieldValue as string), value: fieldValue }],
				},
			};
		});
		nonRequiredFieldsItem = _.pickBy(
			_.mapValues(nonRequiredFieldsItem, (item, key) => {
				const value = _.get(formValues, key);
				const baseItem = _.omit(item, 'x-disabled') as AnyRecord;

				if (key === schemaKeys.memberId && !isPayerSelected) {
					return {
						...baseItem,
						'x-component': 'Select',
						'x-component-props': {
							placeholder: 'Select a payer first',
							options: [],
						},
					};
				}

				if (_.isNil(value) || value === '') {
					return {
						...baseItem,
						'x-component': baseItem['x-component'],
						'x-component-props': baseItem['x-component-props'],
					};
				}

				return {
					...baseItem,
					'x-component': 'Select',
					'x-component-props': {
						options: [{ label: getLabelForValue(key, value), value }],
					},
				};
			}),
			_.identity,
		);
	}
	const searchReqFieldRow = createFormFieldRow(
		'patientReqFieldRow',
		{
			[schemaKeys.patientReqFields]: patientSection?.[schemaKeys.patientReqFields],
		},
		0,
	);
	const reqFieldRows = createFormFieldRow('reqFieldRows', reqPatientFields, 1);
	_.set(schema, patientSectionPath, { ...searchReqFieldRow, ...reqFieldRows, ...extraFields });
	const collapsePath = _.concat(patientSectionPath, ['nonRequiredFields', 'properties', 'firstCollapse', 'properties']);
	const nonRequiredFieldsSection = createFormFieldRow('nonRequiredFields', nonRequiredFieldsItem);
	_.set(schema, collapsePath, { ...nonRequiredFieldsSection });
};

/**
 * Compute required fields from payer data and selected combinations
 */
const computeRequiredFields = (
	payerData: Payers.IPayerMeta,
	selectedRequiredFields: EligibilityRequestForm.IRequiredFieldsGrouping,
): Record<keyof EligibilityRequestForm.IFormModel, boolean> => {
	const eligibilityMeta = payerData?.payerMeta?.eligibilityMeta;
	const patientFieldCombinations = eligibilityMeta?.requiredFieldCombinations?.patient;
	const providerFieldCombinations = eligibilityMeta?.requiredFieldCombinations?.provider;
	const patientRequiredFieldsCombination = getRequiredFieldsCombination(patientFieldCombinations);
	const providerRequiredFieldsCombination = getRequiredFieldsCombination(providerFieldCombinations);
	return getRequiredFields(
		JSON.stringify([
			...JSON.parse((selectedRequiredFields.patient || _.first(patientRequiredFieldsCombination)?.value) ?? JSON.stringify('')),
			...JSON.parse((selectedRequiredFields.provider || _.first(providerRequiredFieldsCombination)?.value) ?? JSON.stringify('')),
		]),
	);
};

const generateSchema = ({
	tenantPayerId,
	metadata,
	selectedRequiredFields,
	isOpenCollapsed,
	payerData,
	formValues,
	formType,
	patientCoverages,
}: {
	// todo
	tenantPayerId: string;
	metadata: AnyType;
	selectedRequiredFields: EligibilityRequestForm.IRequiredFieldsGrouping;
	isOpenCollapsed?: boolean;
	payerData: Payers.IPayerMeta;
	formValues: EligibilityRequestForm.IFormModel;
	formType?: EligibilityRequestForm.IFormType;
	patientCoverages?: Insurance.ICoverageResponseModel[];
}): IFormilySchema => {
	const isPatientForm = formType === PATIENT_FORM_TYPE;
	const isCoverageForm = formType === COVERAGE_FORM_TYPE;
	const disablePatientFields = isPatientForm || isCoverageForm;
	const formCollapse = FormCollapse.createFormCollapse();
	const eligibilityMeta = payerData?.payerMeta?.eligibilityMeta;
	const currentProviderPayerMeta = getProviderIdentifiersById(
		_.get(formValues, schemaKeys.npi) || '',
		payerData?.providerPayerMeta?.data,
	);
	const patientFieldCombinations = eligibilityMeta?.requiredFieldCombinations?.patient;
	const providerFieldCombinations = eligibilityMeta?.requiredFieldCombinations?.provider;
	const encounterFieldCombinations = eligibilityMeta?.requiredFieldCombinations?.encounter;
	const patientRequiredFieldsCombination = getRequiredFieldsCombination(patientFieldCombinations);
	const providerRequiredFieldsCombination = getRequiredFieldsCombination(providerFieldCombinations);
	const encounterRequiredFieldsCombinations = getRequiredFieldsCombination(encounterFieldCombinations);
	const requiredFields = getRequiredFields(
		JSON.stringify([
			...JSON.parse((selectedRequiredFields.patient || _.first(patientRequiredFieldsCombination)?.value) ?? JSON.stringify('')),
			...JSON.parse((selectedRequiredFields.provider || _.first(providerRequiredFieldsCombination)?.value) ?? JSON.stringify('')),
			...JSON.parse(_.first(encounterRequiredFieldsCombinations)?.value ?? JSON.stringify('')),
		]),
	);

	// service type
	const serviceTypeConfig = _.get(eligibilityMeta?.fieldConfig, fieldsConfigMapping.serviceType);
	const maxServiceTypeCount = serviceTypeConfig?.max_items || DEFAULT_MAX_SERVICE_TYPE_COUNT;
	const minServiceTypeCount = serviceTypeConfig?.min_items || DEFAULT_MIN_SERVICE_TYPE_COUNT;

	const schema = {
		title: 'Eligibility Form',
		type: 'object',
		properties: {
			formWrapper: {
				type: 'void',
				'x-component': 'Space',
				'x-component-props': {
					direction: 'vertical',
					size: 16,
					className: ANT_FORMILY_FORM_WRAPPER_CLASS,
				},
				properties: {
					[formSections.payer.key]: {
						type: 'void',
						'x-component': 'Card',
						'x-component-props': {
							title: formSections.payer.title,
							className: ANT_FORMILY_CARD_CLASS,
						},
						properties: {
							fieldsWrapper: {
								type: 'void',
								'x-component': 'div',
								properties: createFormFieldRow('payerFieldRow', {
									[schemaKeys.payerId]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.payerId.title,
										'x-component': isPatientForm ? 'PatientCoveragePayers' : 'SelectedPayers',
										'x-component-props': {
											placeholder: ELIGIBILITY_FORM_FIELDS.payerId.placeholder,
											...(formType?.patient
												? {
														patientCoverages,
														metadata,
													}
												: {
														forEligibility: true,
														selectedPayers: metadata.selectedPayers,
													}),
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: ELIGIBILITY_FORM_FIELDS.payerId.info },
										'x-validator': [
											{
												required: requiredFields.payerId,
												message: ELIGIBILITY_FORM_FIELDS.payerId.errorMessage,
											},
										],
									},
								}),
							},
						},
					},
					[formSections.provider.key]: {
						type: 'void',
						'x-component': 'Card',
						'x-component-props': {
							title: formSections.provider.title,
							className: ANT_FORMILY_CARD_CLASS,
						},
						properties: {
							fieldsWrapper: {
								type: 'void',
								'x-component': 'div',
								properties: {
									[schemaKeys.providerReqFields]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.providerReqFields.title,
										'x-hidden': _.size(providerRequiredFieldsCombination) <= 1,
										'x-index': 1,
										'x-component': 'Select',
										'x-decorator': 'FormItem',
										'x-decorator-props': {
											className: searchFieldLabelClass,
										},
										'x-component-props': {
											options: providerRequiredFieldsCombination,
											placeholder: ELIGIBILITY_FORM_FIELDS.providerReqFields.placeholder,
										},
									},
									[schemaKeys.npi]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.npi.title,
										'x-index': 2,
										'x-component': 'ProviderNpis',
										'x-component-props': {
											tenantPayerId,
											metadata,
											payersMeta: payerData,
											placeholder: ELIGIBILITY_FORM_FIELDS.npi.placeholder,
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: ELIGIBILITY_FORM_FIELDS.npi.info },
										'x-validator': [
											{
												required: requiredFields.npi,
												message: ELIGIBILITY_FORM_FIELDS.npi.errorMessage,
											},
										],
									},
								},
							},
						},
					},
					[formSections.patientDetails.key]: {
						type: 'void',
						'x-component': 'Card',
						'x-component-props': {
							title: formSections.patientDetails.title,
							className: ANT_FORMILY_CARD_CLASS,
						},
						properties: {
							fieldsWrapper: {
								type: 'void',
								'x-component': 'div',
								'x-component-props': {
									className: 'patient-section',
								},
								properties: {
									[schemaKeys.patientReqFields]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.patientReqFields.title,
										'x-hidden': _.size(patientRequiredFieldsCombination) <= 1,
										'x-index': patientFieldsIndex.patientReqFields,
										'x-component': 'Select',
										'x-decorator': 'FormItem',
										'x-decorator-props': {
											className: searchFieldLabelClass,
										},
										'x-component-props': {
											options: patientRequiredFieldsCombination,
											placeholder: ELIGIBILITY_FORM_FIELDS.patientReqFields.placeholder,
										},
									},
									[schemaKeys.firstName]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.firstName.title,
										'x-component': 'Input',
										'x-component-props': {
											placeholder: ELIGIBILITY_FORM_FIELDS.firstName.placeholder,
										},
										'x-decorator': 'FormItem',
										'x-index': requiredFields.firstName ? patientFieldsIndex.firstName : undefined,
										'x-validator': [
											{
												required: requiredFields.firstName,
												message: ELIGIBILITY_FORM_FIELDS.firstName.errorMessage,
											},
											FIELD_VALIDATORS.patientFirstName,
										],
										'x-disabled': disablePatientFields && requiredFields.firstName,
									},
									[schemaKeys.lastName]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.lastName.title,
										'x-component': 'Input',
										'x-component-props': {
											placeholder: ELIGIBILITY_FORM_FIELDS.lastName.placeholder,
										},
										'x-decorator': 'FormItem',
										'x-index': requiredFields.lastName ? patientFieldsIndex.lastName : undefined,
										'x-validator': [
											{
												required: requiredFields.lastName,
												message: ELIGIBILITY_FORM_FIELDS.lastName.errorMessage,
											},
											FIELD_VALIDATORS.patientLastName,
										],
										'x-disabled': disablePatientFields && requiredFields.lastName,
									},

									[schemaKeys.dateOfBirth]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.dateOfBirth.title,
										'x-component': 'DatePicker',
										'x-component-props': {
											placeholder: ELIGIBILITY_FORM_FIELDS.dateOfBirth.placeholder,
											disabledDate: disableFutureDates,
											inputDateFormat: YEAR_MONTH_DATE_FORMAT,
											allowClear: true,
										},
										'x-decorator': 'FormItem',
										'x-index': requiredFields.dateOfBirth ? patientFieldsIndex.dateOfBirth : undefined,
										'x-validator': [
											{
												required: requiredFields.dateOfBirth,
												message: ELIGIBILITY_FORM_FIELDS.dateOfBirth.errorMessage,
											},
										],
										'x-disabled': disablePatientFields && requiredFields.dateOfBirth,
									},
									[schemaKeys.relation]: {
										type: 'string',
										'x-component': 'Select',
										'x-decorator': 'FormItem',
										title: ELIGIBILITY_FORM_FIELDS.relation.title,
										'x-component-props': {
											options: ELIGIBILITY_PATIENT_RELATIONSHIP_OPTIONS,
											placeholder: ELIGIBILITY_FORM_FIELDS.relation.placeholder,
										},
										'x-validator': [
											{
												required: requiredFields.relation,
												message: ELIGIBILITY_FORM_FIELDS.relation.errorMessage,
											},
										],
										'x-disabled': disablePatientFields && requiredFields.relation,
									},

									nonRequiredFields: {
										type: 'void',
										'x-component': 'FormCollapse',
										'x-component-props': {
											...formCollapse,
											defaultActiveKey: isOpenCollapsed ? ['firstCollapse'] : [],
										},
										properties: {
											firstCollapse: {
												type: 'void',
												'x-component': 'FormCollapse.CollapsePanel',
												'x-component-props': {
													header: 'Additional Fields',
												},
												properties: {},
											},
										},
									},
								},
							},
						},
					},
					[formSections.encounterDetails.key]: {
						type: 'void',
						'x-component': 'Card',
						'x-component-props': {
							title: formSections.encounterDetails.title,
							className: ANT_FORMILY_CARD_CLASS,
						},
						properties: {
							fieldsWrapper: {
								type: 'void',
								'x-component': 'div',
								properties: createFormFieldRow('serviceFieldRow', {
									[schemaKeys.serviceStartDate]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.serviceStartDate.title,
										'x-component': 'TooltipWrapper',
										'x-component-props': {
											component: 'DatePicker',
											placeholder: ELIGIBILITY_FORM_FIELDS.serviceStartDate.placeholder,
											inputDateFormat: YEAR_MONTH_DATE_FORMAT,
											displayDateFormat: MONTH_DATE_YEAR_SLASH_FORMATS,
											allowClear: false,
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: ELIGIBILITY_FORM_FIELDS.serviceStartDate.info },
										'x-validator': [
											{
												required: requiredFields.serviceStartDate,
												message: ELIGIBILITY_FORM_FIELDS.serviceStartDate.errorMessage,
											},
											{
												validator: (value: string, _rule: AnyType, ctx: AnyType) => {
													const endDate = ctx?.form?.values?.serviceEndDate;
													if (!value) {
														return '';
													}
													return validateServiceStartDate(value, endDate, true);
												},
											},
										],
										'x-reactions': [
											{
												dependencies: [schemaKeys.serviceStartDate],
												fulfill: {
													run: "$form.validate('serviceEndDate').catch(() => {})",
												},
											},
										],
									},
									[schemaKeys.serviceEndDate]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.serviceEndDate.title,
										'x-component': 'TooltipWrapper',
										'x-component-props': {
											component: 'DatePicker',
											placeholder: ELIGIBILITY_FORM_FIELDS.serviceEndDate.placeholder,
											inputDateFormat: YEAR_MONTH_DATE_FORMAT,
											displayDateFormat: MONTH_DATE_YEAR_SLASH_FORMATS,
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: ELIGIBILITY_FORM_FIELDS.serviceEndDate.info },

										'x-validator': [
											{
												required: requiredFields.serviceEndDate,
												message: ELIGIBILITY_FORM_FIELDS.serviceEndDate.errorMessage,
											},
											{
												validator: (value: string, _rule: AnyType, ctx: AnyType) => {
													const startDate = ctx?.form?.values?.serviceStartDate;
													if (!value) {
														return '';
													}
													return validateServiceEndDate(value, startDate, true);
												},
											},
										],
										'x-reactions': [
											{
												dependencies: [schemaKeys.serviceEndDate],
												fulfill: {
													run: "$form.validate('serviceStartDate').catch(() => {})",
												},
											},
										],
									},
									[schemaKeys.serviceType]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.serviceType.title,
										'x-component': 'ServiceTypeDropdown',
										'x-component-props': {
											eligibilityMeta,
											options: metadata?.serviceTypesOptions || [],
											placeholder: ELIGIBILITY_FORM_FIELDS.serviceType.placeholder,
											maxCount: maxServiceTypeCount,
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: ELIGIBILITY_FORM_FIELDS.serviceType.info },
										'x-validator': [
											{
												required: requiredFields.serviceType,
												defaultServiceTypeValue: [DEFAULT_ELIGIBILITY_SERVICE_TYPE_CODE],
												maxServiceTypeCount,
												message: ELIGIBILITY_FORM_FIELDS.serviceType.errorMessage,
												validator: serviceTypeValidator,
											},
											{
												maxItems: maxServiceTypeCount,
												message: `You can't select more than ${maxServiceTypeCount} service types.`,
											},
											{
												minItems: minServiceTypeCount,
												message: `Please select at least ${minServiceTypeCount} service types.`,
											},
										],
									},
									[schemaKeys.locationId]: {
										type: 'string',
										title: ELIGIBILITY_FORM_FIELDS.locationId.title,
										'x-component': 'Select',
										'x-component-props': {
											placeholder: ELIGIBILITY_FORM_FIELDS.locationId.placeholder,
											options: metadata?.locationGroupOptions,
											allowClear: true,
										},
										'x-decorator': 'FormItem',
										'x-validator': [
											{
												required: requiredFields.locationId,
												message: ELIGIBILITY_FORM_FIELDS.locationId.errorMessage,
											},
										],
									},
								}),
							},
						},
					},
				},
			},
		},
	};

	updatePatientSection(schema, requiredFields, eligibilityMeta, disablePatientFields, formValues);
	updateProviderSection(schema, requiredFields, currentProviderPayerMeta);
	return schema;
};

export {
	schemaKeys,
	fieldsConfigMapping,
	patientSectionPath,
	providerSectionPath,
	formFieldsToTranslate,
	providerIdentifierKeys,
	requiredFieldsMapping,
	generateSchema,
	computeRequiredFields,
	patientFieldsIndex,
	createFormFieldRow,
	serviceTypeValidator,
	updatePatientSection,
	updateProviderSection,
	getProviderIdentifierKeys,
	getRequiredFieldsCombination,
	validateServiceEndDate,
	validateServiceStartDate,
};
